<template>
  <sui-segment attached="bottom">
    <p :class="{'ui warning message': !subscription.active && isExpired}" v-if="!tariff.isFree">
      <span v-if="isTrial">
        <b>Trial period</b> will be over
        <span>&nbsp;on&nbsp;<b>{{momentFormat(account.trialUntil, 'LL')}}</b>.</span>
        <br>
      </span>
      <b>{{oldPlan.constructor.tariffTitle}}</b> subscription
      <span v-show="!subscription.active && isExpired">was over</span>
      <span v-show="!subscription.active && !isExpired">will be over</span>
      <span v-show="subscription.active && !isExpired">will automatically renew</span>
      <span v-show="subscription.active && isExpired">failed to renew</span>
      <span>&nbsp;on&nbsp;<b>{{momentFormat(account.expiration, 'LL')}}</b>.</span>
    </p>
    <sui-form warning :error="hasError">
      <sui-segment emphasis="secondary">
        <sui-form-fields>
          <sui-form-field :width="5">
            <label>Plan</label>
            <sui-dropdown :class="{disabled: !access.tariffManagement}" v-model="newPlanId" :options="planList" selection search />
            <p>
              <small>${{newPlan ? newPlan.price : 0}} {{newPlan.period === '1y' ? 'annualy' : 'monthly'}}</small>
            </p>
          </sui-form-field>
          <sui-form-field :width="3">
            <label>Extra members</label>
            <div class="ui action right labeled input">
              <input v-model.number="newExtraMembers" :disabled="!newPlan.limits.invite || !access.tariffManagement" />
              <div class="ui vertical buttons">
                <sui-button icon="plus" :disabled="!newPlan.limits.invite || !access.tariffManagement" style="font-size: .5rem; border-bottom-right-radius: 0;" @click.prevent="newExtraMembers = newExtraMembers + 1" />
                <sui-button icon="minus" :disabled="!newPlan.limits.invite || !access.tariffManagement" style="font-size: .5rem; border-top-right-radius: 0;" @click.prevent="newExtraMembers = newExtraMembers + (newExtraMembers > 0 ? -1 : 0)" />
              </div>
            </div>
            <p>
              <small>${{newOptions[0] ? newExtraMembers * newOptions[0].price : 0}} {{newPlan.period === '1y' ? 'annualy' : 'monthly'}}</small>
            </p>
          </sui-form-field>
          <sui-form-field :width="3">

          </sui-form-field>
          <sui-form-field :width="5">
            <label>Total</label>
            <sui-label class="fluid" size="big" style="margin: 0;">
              ${{total}} {{newPlan.period === '1y' ? 'annualy' : 'monthly'}}
            </sui-label>
            <p style="color: #090" v-show="newPlan.period === '1y'">
              <small>you{{isChanged || !subscription.active ? ' will' : ''}} save ${{discount}} {{newPlan.period === '1y' ? 'annualy' : 'monthly'}}</small>
            </p>
          </sui-form-field>
        </sui-form-fields>
        <sui-message size="small" warning v-if="isDowngrade">
          <span v-show="newPlan.constructor.order !== oldPlan.constructor.order">{{oldPlan.constructor.tariffTitle}}</span>
          <span v-show="newExtraMembers !== oldExtraMembers">
            <span v-show="newPlan.constructor.order !== oldPlan.constructor.order">&nbsp;with</span>
            {{oldExtraMembers}} extra users
          </span>
          will stay active until the end of the paid period: {{momentFormat(account.expiration, 'LL')}}.
        </sui-message>
      </sui-segment>
      <sui-form-field>
        <sui-button color="green" :disabled="isSubscribeDisabled" @click.prevent="subscribe({startTrial: true})" v-if="isSubscribeAvailable && hasTrial">Try for free (14 days)</sui-button>
        <sui-button-group color="green" v-show="isSubscribeAvailable">
          <sui-button :disabled="isSubscribeDisabled" @click.prevent="subscribe()">Subscribe</sui-button>
          <sui-dropdown class="icon" :disabled="(subscription.active || newPlan.isFree) && !isChanged" icon="dropdown" button @click.prevent>
            <sui-dropdown-menu style="left: 0; right: auto;">
              <sui-dropdown-item @click="copySubscribeLink()" data-goal="SETTINGS_PLANS_SUBSCRIBE_LINK">
                Copy the link for subscription<br>
                <small class="dropdown-item-description">If your subscription will be paid by someone else</small>
              </sui-dropdown-item>
            </sui-dropdown-menu>
          </sui-dropdown>
        </sui-button-group>
        <sui-button color="green" :disabled="!isChanged || !access.tariffManagement" v-show="!isSubscribeAvailable" @click.prevent="changePlan()">
          {{action}} plan
        </sui-button>
        <sui-button color="blue" :disabled="!isChanged" basic @click.prevent="resetChanges()">
          Reset changes
        </sui-button>
        <sui-button-group class="right floated" color="blue" v-show="subscription.active">
          <sui-button :disabled="isChanged || !access.tariffManagement" @click.prevent="$refs.updateDropdown.handleClick($event)">Manage subscription</sui-button>
          <sui-dropdown class="icon" :disabled="isChanged || !access.tariffManagement" icon="dropdown" ref="updateDropdown" button @click.prevent>
            <sui-dropdown-menu>
              <sui-dropdown-item :disabled="!subscription.updateUrl" @click="updatePayment()" data-goal="SETTINGS_PLANS_UPDATE_PAYMENT">
                Update the payment method
              </sui-dropdown-item>
              <sui-dropdown-item :disabled="!subscription.updateUrl" @click="$copyText(subscription.updateUrl)" data-goal="SETTINGS_PLANS_UPDATE_PAYMENT_LINK">
                Copy the link for updating the payment method<br>
                <small class="dropdown-item-description">If your subscription is paid by someone else</small>
              </sui-dropdown-item>
              <sui-dropdown-item color="red" @click="cancelRenewal()" data-goal="SETTINGS_PLANS_CANCEL_RENEWAL">
                Cancel the renewal<br>
                <small class="dropdown-item-description">The current subscription will stay active until the end of the paid period.</small>
              </sui-dropdown-item>
            </sui-dropdown-menu>
          </sui-dropdown>
        </sui-button-group>
      </sui-form-field>
      <sui-message v-if="error" error dismissable @dismiss="error = null">
        {{error}}
      </sui-message>
    </sui-form>
    <h3 class="ui header">Plan comparison</h3>
    <sui-table compact celled definition size="large">
      <sui-table-header class="full-width">
        <sui-table-row text-align="center">
          <th></th>
          <sui-table-header-cell :positive="tariff.isFree" :width="3">
            {{freePlan.name}}
          </sui-table-header-cell>
          <sui-table-header-cell :positive="tariff.isStarter" :width="3">
            {{starterPlan.name}}
          </sui-table-header-cell>
          <sui-table-header-cell :positive="tariff.isEarlyBird" :width="3" v-if="tariff.isEarlyBird">
            {{earlyBirdPlan.name}}
          </sui-table-header-cell>
          <sui-table-header-cell :positive="tariff.isPersonal" :width="3">
            {{personalPlan.name}}
          </sui-table-header-cell>
          <sui-table-header-cell :positive="tariff.isBusiness" :width="3">
            {{businessPlan.name}}
          </sui-table-header-cell>
        </sui-table-row>
      </sui-table-header>
      <sui-table-body>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue server icon" /> Hosts limit
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <span v-if="freePlan.limits.hosts">{{freePlan.limits.hosts}}</span>
            <span v-if="!freePlan.limits.hosts">3 temporary</span>
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            {{starterPlan.limits.hosts}}
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            {{earlyBirdPlan.limits.hosts}}
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            {{personalPlan.limits.hosts}}
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            {{businessPlan.limits.hosts}}
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue sort amount down icon" /> Priority of processes
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            Low
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            Medium
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            Medium
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            Medium
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            High
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue chart bar icon" /> Metric retention
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            1 {{freePlan.limits.metricsRetention}}
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            1 {{starterPlan.limits.metricsRetention}}
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            1 {{earlyBirdPlan.limits.metricsRetention}}
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            1 {{personalPlan.limits.metricsRetention}}
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            1 {{businessPlan.limits.metricsRetention}}
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue upload icon" /> Vertical scaling</sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <i class="large icon" :class="freePlan.limits.hosts ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <i class="large icon" :class="starterPlan.limits.hosts ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <i class="large icon" :class="earlyBirdPlan.limits.hosts ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <i class="large icon" :class="personalPlan.limits.hosts ? 'green checkmark' : 'red checkmark'" />
            </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <i class="large icon" :class="businessPlan.limits.hosts ? 'green checkmark' : 'red checkmark'" />
            </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue sitemap icon" /> Horizontal scaling
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <i class="large icon" :class="freePlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <i class="large icon" :class="starterPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <i class="large icon" :class="earlyBirdPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <i class="large icon" :class="personalPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <i class="large icon" :class="businessPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue scale balance icon" /> Load Balancing
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <i class="large icon" :class="freePlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <i class="large icon" :class="starterPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <i class="large icon" :class="earlyBirdPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <i class="large icon" :class="personalPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <i class="large icon" :class="businessPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue share square icon" /> Container migration
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <i class="large icon" :class="freePlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <i class="large icon" :class="starterPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <i class="large icon" :class="earlyBirdPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <i class="large icon" :class="personalPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <i class="large icon" :class="businessPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue database icon" /> DB Replication
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <i class="large icon" :class="freePlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <i class="large icon" :class="starterPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <i class="large icon" :class="earlyBirdPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <i class="large icon" :class="personalPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <i class="large icon" :class="businessPlan.limits.hosts > 1 ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue anchor icon" /> Webhooks
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <i class="large icon" :class="freePlan.limits.hooks ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <i class="large icon" :class="starterPlan.limits.hooks ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <i class="large icon" :class="earlyBirdPlan.limits.hooks ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <i class="large icon" :class="personalPlan.limits.hooks ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <i class="large icon" :class="businessPlan.limits.hooks ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing data-inverted :data-tooltip="`You can set global access rules\nfor members and roles\naffecting all objects`">
            <i class="blue user lock icon" /> Global access control
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <i class="large icon" :class="freePlan.limits.invite ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <i class="large icon" :class="starterPlan.limits.invite ? 'green checkmark' : 'red checkmark'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <i class="large icon" :class="earlyBirdPlan.limits.invite ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <i class="large icon" :class="personalPlan.limits.invite ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <i class="large icon" :class="businessPlan.limits.invite ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing data-inverted :data-tooltip="`You can set local access rules\nfor members and roles\naffecting selected project, host or service`">
            <i class="blue user lock icon" /> Local access control
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <i class="large icon" :class="freePlan.limits.localRules ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <i class="large icon" :class="starterPlan.limits.localRules ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <i class="large icon" :class="earlyBirdPlan.limits.localRules ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <i class="large icon" :class="personalPlan.limits.localRules ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <i class="large icon" :class="businessPlan.limits.localRules ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue directions icon" /> Log redirection
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <i class="large icon" :class="freePlan.limits.logProvider ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <i class="large icon" :class="starterPlan.limits.logProvider ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <i class="large icon" :class="earlyBirdPlan.limits.logProvider ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <i class="large icon" :class="personalPlan.limits.logProvider ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <i class="large icon" :class="businessPlan.limits.logProvider ? 'green checkmark' : 'red times'" />
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue user plus icon" /> Extra members
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            <span v-if="freePlan.limits.invite">
              ${{extraMembersAnnualOption.price}} <small>per member annualy</small><br>
              ${{extraMembersOption.price}} <small>per member monthly</small>
            </span>
            <i class="large red times icon" v-if="!freePlan.limits.invite" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            <span v-if="starterPlan.limits.invite">
              ${{extraMembersAnnualOption.price}} <small>per member annualy</small><br>
              ${{extraMembersOption.price}} <small>per member monthly</small>
            </span>
            <i class="large red times icon" v-if="!starterPlan.limits.invite" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            <span v-if="earlyBirdPlan.limits.invite">
              ${{extraMembersAnnualOption.price}} <small>per member annualy</small><br>
              ${{extraMembersOption.price}} <small>per member monthly</small>
            </span>
            <i class="large red times icon" v-if="!earlyBirdPlan.limits.invite" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            <span v-if="personalPlan.limits.invite">
              ${{extraMembersAnnualOption.price}} <small>per member annualy</small><br>
              ${{extraMembersOption.price}} <small>per member monthly</small>
            </span>
            <i class="large red times icon" v-if="!personalPlan.limits.invite" />
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            <span v-if="businessPlan.limits.invite">
              ${{extraMembersAnnualOption.price}} <small>per member annualy</small><br>
              ${{extraMembersOption.price}} <small>per member monthly</small>
            </span>
            <i class="large red times icon" v-if="!businessPlan.limits.invite" />
          </sui-table-cell>
        </sui-table-row>
        <sui-table-row text-align="center">
          <sui-table-cell text-align="left" collapsing>
            <i class="blue dollar sign icon" /> Price
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isFree">
            Free
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isStarter">
            ${{starterAnnualPlan.price}} <small>annually</small><br>
            ${{starterPlan.price}} <small>monthly</small>
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isEarlyBird" v-if="tariff.isEarlyBird">
            ${{earlyBirdAnnualPlan.price}} <small>annually</small><br>
            ${{earlyBirdPlan.price}} <small>monthly</small>
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isPersonal">
            ${{personalAnnualPlan.price}} <small>annually</small><br>
            ${{personalPlan.price}} <small>monthly</small>
          </sui-table-cell>
          <sui-table-cell :positive="tariff.isBusiness">
            ${{businessAnnualPlan.price}} <small>annually</small><br>
            ${{businessPlan.price}} <small>monthly</small>
          </sui-table-cell>
        </sui-table-row>
      </sui-table-body>
    </sui-table>
    <small>
      <i class="red check icon" /> using demo hosts
    </small>
  </sui-segment>
</template>

<script>
  import moment from 'moment'
  import {mapGetters} from 'vuex'
  import api from '@/api'
  import store from '@/store'
  import utils from '@/utils'

  export default {
    name: 'SettingsPlansView',
    data() {
      const oldPlanId = store.getters.tariff.id
      const oldOptions = store.getters.account.subscription.options
      const oldExtraMembers = oldOptions[0] ? oldOptions[0].amt : 0
      return {
        oldPlanId,
        oldExtraMembers,
        newPlanId: oldPlanId,
        newExtraMembers: oldExtraMembers,
        error: null,
      }
    },
    computed: {
      ...mapGetters(['access', 'account', 'config', 'tariff', 'tariffs', 'tariffsById', 'tariffsByName']),
      hasError() {
        return Boolean(this.error)
      },
      subscription() {
        return this.account.subscription
      },
      freePlan() {
        return this.tariffsById['58c0e161b2eac50001858817']
      },
      earlyBirdPlan() {
        return this.tariffsById['58c0e19ab2eac50001858819']
      },
      earlyBirdAnnualPlan() {
        return this.tariffsById['59e83bd1d38e7200013bd0b1']
      },
      starterPlan() {
        return this.tariffsById['5f731985b62b534e37ef5bf3']
      },
      starterAnnualPlan() {
        return this.tariffsById['5f731985b62b534e37ef5bf4']
      },
      personalPlan() {
        return this.tariffsById['5b9f4e6cb42e24e06a258519']
      },
      personalAnnualPlan() {
        return this.tariffsById['5b9f4e86b42e24770225851a']
      },
      businessPlan() {
        return this.tariffsById['5b6d7dc6bf716bde9a2217ff']
      },
      businessAnnualPlan() {
        return this.tariffsById['5b6d7de1bf716b8b96221800']
      },
      extraMembersOption() {
        return this.tariffsById['5b7176b6bf716b53f1221803']
      },
      extraMembersAnnualOption() {
        return this.tariffsById['5b7176c8bf716b010c221804']
      },
      isAnnual() {
        return this.tariff.name.toLowerCase().includes('annual')
      },
      isExpired() {
        return this.account && this.account.expiration && (new Date(this.account.expiration)).getTime() < (new Date()).getTime()
      },
      isChanged() {
        return this.newPlanId !== this.oldPlanId || this.newExtraMembers !== this.oldExtraMembers
      },
      isSubscribeAvailable() {
        return this.oldPlan.isFree || (!this.newPlan.isFree && !this.subscription.active)
      },
      isSubscribeDisabled() {
        return (this.subscription.active || this.newPlan.isFree) && !this.isChanged || !this.access.tariffManagement
      },
      planList() {
        const plans = this.tariffs.filter(tariff => !tariff.constructor.hidden && tariff.constructor.groupName === 'plan')
        if (this.tariff.isEarlyBird) {
          plans.push(this.oldPlan)
        }
        const orderedPlans = plans.sort((plan1, plan2) => plan1.constructor.order > plan2.constructor.order ? 1 : -1)
        const planList = orderedPlans.map(tariff => {
          return {
            value: tariff.id,
            text: tariff.constructor.tariffTitle,
            icon: tariff.icon,
            disabled: !tariff.active,
          }
        })
        return planList
      },
      oldPlan() {
        return this.tariffsById[this.oldPlanId]
      },
      newPlan() {
        return this.tariffsById[this.newPlanId]
      },
      newOptions() {
        return this.newPlan._options
      },
      tariffData() {
        return {
          tariff: this.newPlanId,
          options: (this.newOptions.length && this.newExtraMembers) ? [{id: this.newOptions[0].id, amt: this.newExtraMembers}] : [],
        }
      },
      total() {
        const planPrice = this.newPlan ? this.newPlan.price : 0
        const optionsPrice = this.newOptions.length ? this.newExtraMembers * this.newOptions[0].price : 0
        return planPrice + optionsPrice
      },
      discount() {
        return Math.round(this.total / 5)
      },
      isUpgrade() {
        return this.newPlan.constructor.order > this.oldPlan.constructor.order || this.newExtraMembers > this.oldExtraMembers
      },
      isDowngrade() {
        return this.newPlan.constructor.order < this.oldPlan.constructor.order || this.newExtraMembers < this.oldExtraMembers
      },
      action() {
        return this.oldPlanId === this.newPlanId ? 'Update' : 'Change'
      },
      isTrial() {
        return moment(this.account.trialUntil).diff() > 0
      },
      hasTrial() {
        return !this.account.trialUntil
      },
    },
    watch: {
      newPlan() {
        if (!this.newOptions.length) {
          this.newExtraMembers = 0
        }
      },
    },
    methods: {
      resetChanges() {
        this.newPlanId = this.oldPlanId
        this.newExtraMembers = this.oldExtraMembers
      },
      copySubscribeLink(options = {startTrial: false}) {
        api.createSubscriptionLink({...this.tariffData, ...options}).then(this.$copyText).catch(error => {
          this.error = error
        })
      },
      subscribe(options = {startTrial: false}) {
        if (options.startTrial) {
          utils.reachGoal(`SETTINGS_PLANS_START_TRIAL`)
        }
        utils.reachGoal(`SETTINGS_PLANS_SUBSCRIBE`)
        utils.reachGoal(`SETTINGS_PLANS_SUBSCRIBE_${this.newPlan.name.replace(' ', '').toUpperCase()}`)
        api.createSubscriptionLink({...this.tariffData, ...options}).then(subscribeLink => {
          window.Paddle.Checkout.open({
            override: subscribeLink
          })
        }).catch(error => {
          this.error = error
        })
      },
      changePlan() {
        utils.reachGoal(`SETTINGS_PLANS_CHANGE_${this.newPlan.name.replace(' ', '').toUpperCase()}`)
        return this.$modals.confirm.open({
          text: `You are going to ${this.action.toLowerCase()} your plan.`,
          callback: () => {
            this.error = null
            api.updateScubscription(this.tariffData).catch(error => {
              this.error = error
            })
          },
        })
      },
      updatePayment() {
        window.Paddle.Checkout.open({
          override: this.subscription.updateUrl,
        })
      },
      cancelRenewal() {
        return this.$modals.confirm.open({
          text: '<p>You are going to cancel automatical renewal of your subscription.</p><p>It will stay active until the end of the paid period.</p><p>You won\'t be able to change your plan or options during this time!</p>',
          callback: () => {
            this.error = null
            api.cancelSubscription().catch(error => {
              this.error = error
            })
          },
        })
      },
    },
    mounted() {
      if (!window.Paddle) {
        utils.addScript('https://cdn.paddle.com/paddle/paddle.js').then(() => {
          window.Paddle.Setup({
            vendor: 29325,
            debug: this.config.debug,
          })
        })
      }
    },
  }
</script>

<style scoped>

</style>
